import React from "react";
import { Lock, Trash2 } from "lucide-react";
import { IconButton, WithInfo } from "../common";
import { GLOSSARY } from "../glossary";
import { encodeCell, parseQueryRows, withQueryRows } from "./urlQuery";

const cellClass = "block w-full px-2 py-1.5 text-xs font-mono text-gray-900 bg-white border border-gray-2 rounded-md focus:outline-none focus:border-primary disabled:bg-gray-100";

/**
 * Query params table that stays in sync with the raw URL, like Postman.
 * Plain parameters live in the URL; secret ones are kept out of it (stored
 * encrypted in request.query) and added by the server when it calls the API.
 *
 * @param {object} props
 * @param {string} props.url - raw URL, the source of truth for plain parameters
 * @param {Array} props.secrets - request.query entries marked secret
 * @param {Function} props.onChange - ({ url, query }) => void
 */
const QueryParamsEditor = ({ url, secrets = [], onChange, secretMask }) => {
    const rows = parseQueryRows(url);

    const setRows = (next) => onChange({ url: withQueryRows(url, next), query: secrets });
    const setSecrets = (next) => onChange({ url, query: next });

    const updateRow = (index, patch) => setRows(rows.map((row, i) => (i === index ? { ...row, ...patch, hasEquals: true } : row)));

    // Typing in the blank last row adds a parameter to the URL.
    const addRow = (patch) => setRows([...rows, { key: "", value: "", hasEquals: true, ...patch }]);

    const makeSecret = (index) => {
        const row = rows[index];
        onChange({
            url: withQueryRows(url, rows.filter((_, i) => i !== index)),
            query: [...secrets, { key: row.key, value: row.value, secret: true }],
        });
    };

    const makePlain = (index) => {
        const entry = secrets[index];
        // A saved secret can't be shown again, so its value has to be re-entered.
        const value = entry.value === secretMask ? "" : entry.value;
        onChange({
            url: withQueryRows(url, [...rows, { key: encodeCell(entry.key, { name: true }), value: encodeCell(value), hasEquals: true }]),
            query: secrets.filter((_, i) => i !== index),
        });
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-xs text-gray-500">
                        <th className="py-1 pr-2 w-[35%]">Key</th>
                        <th className="py-1 pr-2">Value</th>
                        <th className="py-1 pr-2 w-16"><WithInfo tip={GLOSSARY.mapping.secret}>Secret</WithInfo></th>
                        <th className="w-8" />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={`row-${index}`}>
                            <td className="py-0.5 pr-2">
                                <input className={cellClass} value={row.key} placeholder="Key" onChange={(e) => updateRow(index, { key: encodeCell(e.target.value, { name: true }) })} />
                            </td>
                            <td className="py-0.5 pr-2">
                                <input className={cellClass} value={row.value} placeholder="Value" onChange={(e) => updateRow(index, { value: encodeCell(e.target.value) })} />
                            </td>
                            <td className="py-0.5 pr-2 text-center">
                                <input type="checkbox" title="Keep this value out of the URL and store it encrypted" checked={false} onChange={() => makeSecret(index)} />
                            </td>
                            <td>
                                <IconButton title="Remove parameter" onClick={() => setRows(rows.filter((_, i) => i !== index))}>
                                    <Trash2 size={14} />
                                </IconButton>
                            </td>
                        </tr>
                    ))}
                    {/* Same index-based key as the row it becomes, so focus stays while typing. */}
                    <tr key={`row-${rows.length}`}>
                        <td className="py-0.5 pr-2">
                            <input className={cellClass} value="" placeholder="Add a parameter" onChange={(e) => addRow({ key: encodeCell(e.target.value, { name: true }) })} />
                        </td>
                        <td className="py-0.5 pr-2">
                            <input className={cellClass} value="" placeholder="Value" onChange={(e) => addRow({ value: encodeCell(e.target.value) })} />
                        </td>
                        <td />
                        <td />
                    </tr>
                    {secrets.map((entry, index) => (
                        <tr key={`secret-${index}`} className="bg-gray-50">
                            <td className="py-0.5 pr-2">
                                <div className="flex items-center gap-1">
                                    <Lock size={12} className="text-gray-400 shrink-0" />
                                    <input
                                        className={cellClass}
                                        value={entry.key}
                                        onChange={(e) => setSecrets(secrets.map((s, i) => (i === index ? { ...s, key: e.target.value } : s)))}
                                    />
                                </div>
                            </td>
                            <td className="py-0.5 pr-2">
                                <input
                                    type="password"
                                    className={cellClass}
                                    value={entry.value}
                                    placeholder="Secret value"
                                    onChange={(e) => setSecrets(secrets.map((s, i) => (i === index ? { ...s, value: e.target.value } : s)))}
                                />
                            </td>
                            <td className="py-0.5 pr-2 text-center">
                                <input type="checkbox" title="Move this parameter back into the URL" checked onChange={() => makePlain(index)} />
                            </td>
                            <td>
                                <IconButton title="Remove parameter" onClick={() => setSecrets(secrets.filter((_, i) => i !== index))}>
                                    <Trash2 size={14} />
                                </IconButton>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {secrets.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">Secret parameters aren't shown in the URL; they're stored encrypted and added when the API is called.</p>
            )}
        </div>
    );
};

export default QueryParamsEditor;
